<template>
  <div
    class="preview-btn-fab-actions"
    :class="{ 'no-drawer': !globalStore.boxDetailsDrawer }"
  >
    <PreviewBtnFab
      v-if="!globalStore.boxDetailsDrawer"
      v-tooltip.start="t('node_data_table.actions.show_details')"
      @click="globalStore.boxDetailsDrawer = !globalStore.boxDetailsDrawer"
    >
      <v-icon>far fa-circle-info</v-icon>
    </PreviewBtnFab>
    <template v-if="!isCheckingDeepSign">
      <DeepMenu
        v-if="integrationItems.length > 1"
        v-model="integrationsMenu"
        :close-on-content-click="false"
        :items="integrationItems"
        :list-props="{ density: 'compact' }"
        location="start"
        min-width="200"
      >
        <template #activator="{ props: ActivatorProps }">
          <PreviewBtnFab
            v-tooltip.start="t('node_data_table.actions.group.actions')"
            v-bind="ActivatorProps"
            data-test-id="integrations-menu"
          >
            <v-icon>far fa-ellipsis</v-icon>
          </PreviewBtnFab>
        </template>
      </DeepMenu>
      <PreviewBtnFab
        v-else-if="showDeepSignBtn"
        v-tooltip.start="t('node_data_table.actions.sign')"
        data-test-id="deepSign"
        @click="openDeepSignOrConvertNode"
      >
        <v-icon>
          <img alt="DeepSign" src="/svg/deepsign/sign_Icon_24.svg" />
        </v-icon>
      </PreviewBtnFab>
      <PreviewBtnFab
        v-else-if="showDeepValidatorBtn"
        v-tooltip.start="deepValidator.config.value.title"
        data-test-id="deep-validator-open"
        :disabled="isDeepValidatorLoading"
        :loading="isDeepValidatorLoading"
        @click="openWithDeepValidator"
      >
        <DeepIcon
          class="deep-validator-icon"
          height="24"
          name="deep-validator"
          width="24"
        />
      </PreviewBtnFab>
    </template>
    <PreviewBtnFab
      v-if="canUseNodeOffice(node)"
      v-tooltip.start="t('node_data_table.actions.edit')"
      data-test-id="edit-document"
      @click="openOfficeEditNodeUrl(node)"
    >
      <v-icon>far fa-pen</v-icon>
    </PreviewBtnFab>
    <DeepPayPaymentDialog
      v-if="canNodePayment && orgId && typeId && boxId && node"
      :box-id="boxId"
      :node="node"
      :org-id="orgId"
      :type-id="typeId"
    >
      <template #activator="{ props: ActivatorProps }">
        <PreviewBtnFab
          v-tooltip.start="t('node_data_table.actions.pay')"
          v-bind="ActivatorProps"
          data-test-id="node-payment"
        >
          <DeepAppIcon logo="deeppay" />
        </PreviewBtnFab>
      </template>
    </DeepPayPaymentDialog>
  </div>
</template>

<script lang="ts" setup>
import { toast } from 'vue-sonner'
import { DeepAppIcon, DeepIcon, DeepMenu } from '@deepcloud/deep-ui-lib'
import {
  isNodeMimeTypeDOCX,
  isNodeMimeTypePdf,
  isNodeMimeTypeXLSX,
} from '@deepcloud/deep-ui-utils'
import { useGlobalStore } from '@/stores/global/global'
import { useDeepSign } from '@/composables/deep-sign'
import { useDeepValidatorOpen } from '@/composables/deep-validator'
import { useNodeConvertPdfAndSign } from '@/composables/node/node-convert-pdf-and-sign'
import { useNodePayment } from '@/composables/node/node-payment'
import { useNodePolicy } from '@/composables/node/node-policy'
import { useOffice } from '@/composables/office'
import DeepPayPaymentDialog from '@/components/deeppay/paymentorder/DeepPayPaymentDialog.vue'
import { NODE_ACTIONS } from '@/components/node/node-actions'
import PreviewBtnFab from '@/components/preview/PreviewBtnFab.vue'
import type { Node } from '@deepcloud/api-deep-box'
import type { MenuItem } from '@deepcloud/deep-ui-lib'

const props = defineProps({
  node: {
    type: Object as PropType<Node>,
    required: true,
  },
  orgId: {
    type: String,
    default: undefined,
  },
  typeId: {
    type: String,
    default: undefined,
  },
  boxId: {
    type: String,
    default: undefined,
  },
})

const { t } = useI18n()

const globalStore = useGlobalStore()

const canOpenDocumentOnDeepSign = ref(false)
// integrations stay hidden until the DeepSign check settles, so the inline/menu layout doesn't flip
const isCheckingDeepSign = ref(true)

const { canSign } = useNodePolicy()
const { canNodePayment } = useNodePayment(computed(() => props.node as Node))
const deepValidator = useDeepValidatorOpen()
const isDeepValidatorLoading = ref(false)

const isPdf = computed(() => isNodeMimeTypePdf(props.node.mimeType))

const showDeepSignBtn = computed(
  () =>
    (isPdf.value && canSign(props.node) && canOpenDocumentOnDeepSign.value) ||
    isNodeMimeTypeDOCX(props.node.mimeType) ||
    isNodeMimeTypeXLSX(props.node.mimeType),
)

const showDeepValidatorBtn = computed(() => isPdf.value)

const integrationsMenu = ref(false)
// DeepValidator keeps the menu open while its API call is in flight
watch(isDeepValidatorLoading, (loading) => {
  if (!loading) {
    integrationsMenu.value = false
  }
})

const integrationItems = computed((): MenuItem[] =>
  [
    {
      key: NODE_ACTIONS.SIGN.id,
      id: NODE_ACTIONS.SIGN.id,
      title: t('node_data_table.actions.sign'),
      prepend: { icon: getSignatureStatusIcon(props.node) },
      action: () => {
        openDeepSignOrConvertNode()
        integrationsMenu.value = false
      },
      show: () => showDeepSignBtn.value,
      dataTestId: 'deepSign',
    },
    {
      key: NODE_ACTIONS.DEEP_VALIDATOR_OPEN.id,
      id: NODE_ACTIONS.DEEP_VALIDATOR_OPEN.id,
      title: deepValidator.config.value.title,
      prepend: { deepIcon: NODE_ACTIONS.DEEP_VALIDATOR_OPEN.deepIcon },
      loading: isDeepValidatorLoading.value,
      disabled: isDeepValidatorLoading.value,
      action: openWithDeepValidator,
      show: () => showDeepValidatorBtn.value,
      dataTestId: 'deep-validator-open',
    },
  ].filter((i) => i.show()),
)

const { openOfficeEditNodeUrl, canUseNodeOffice } = useOffice()

const {
  openDeepSignDocument,
  canOpenDocumentOnDeepsign: onCanOpenDocumentOnDeepsign,
  getSignatureStatusIcon,
} = useDeepSign()

const nodeConvertPdfAndSign = useNodeConvertPdfAndSign()

function openDeepSignOrConvertNode() {
  if (
    isNodeMimeTypeDOCX(props.node.mimeType) ||
    isNodeMimeTypeXLSX(props.node.mimeType)
  ) {
    nodeConvertPdfAndSign.doConvertWordToPdfAndSign(props.node)
    return
  }
  if (props.node) {
    openDeepSignDocument(props.node)
  }
}

function openWithDeepValidator() {
  deepValidator.execute({
    node: props.node,
    onLoading: (loading) => {
      isDeepValidatorLoading.value = loading
    },
    onError: (error) => {
      toast.error(deepValidator.getErrorMessage(error))
    },
  })
}

watch(
  () => props.node,
  (newValue) => {
    checkCanOpenOnDeepsign(newValue)
  },
  { immediate: true },
)

async function checkCanOpenOnDeepsign(node: Node) {
  isCheckingDeepSign.value = true
  const canOpen = await onCanOpenDocumentOnDeepsign(node)
  // ignore a stale result when the user navigated to another node meanwhile
  if (node !== props.node) return
  canOpenDocumentOnDeepSign.value = canOpen
  isCheckingDeepSign.value = false
}
</script>

<style scoped lang="scss">
.deepsign-button {
  filter: invert(58%) sepia(92%) saturate(6%) hue-rotate(341deg) brightness(93%)
    contrast(85%);
}

.deep-validator-icon {
  // the icon's single path is green (#41AF00) by default; recolor it to
  // match the plain black look of the DeepSign icon next to it
  :deep(path) {
    fill: #000000;
  }
}

.preview-btn-fab-actions {
  transition: right 200ms ease-in-out;
  z-index: 99;
  position: fixed;
  top: 64px;
  right: 320px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 10px 10px 0 0;

  &.no-drawer {
    right: 0;
  }
}
</style>
